import { gql } from '@apollo/client'
import { useActionMutation, type UseMutationProps } from '@ztechapps/common.hook'
import type { WorkOrder, StateTransitionArgs, DestructedTransitionArgs } from '@ztechapps/common.contract'
import { WorkOrderStatusEnum } from '@ztechapps/common.contract'
import { workOrderDocumentFragment } from './graphql'

/**
 * Mutation to change work order status using the generic state transition.
 * Allows transitioning to any valid state.
 */
export const changeWorkOrderStatusQuery = gql`
  mutation changeWorkOrderStatus($input: StateTransitionArgs!) {
    changeWorkOrderStatus(input: $input) {
      ...workOrderDocumentFragment
    }
  }
  ${workOrderDocumentFragment}
`

/**
 * Hook for managing work order state transitions.
 * Provides convenient wrapper functions that all use the generic changeWorkOrderStatus mutation.
 *
 * @example
 * ```tsx
 * const [{ moveToInProgress, moveToComplete }, { loading, error }] = useWorkOrderStateTransition()
 *
 * // Move to In Progress by _id
 * const result = await moveToInProgress(workOrderId)
 *
 * // Move to Complete
 * const result = await moveToComplete(workOrderId)
 * ```
 */
export const useWorkOrderStateTransition = ({ query, ...props } = {} as UseMutationProps) => {
  query = query ?? changeWorkOrderStatusQuery

  const [_changeStatus, result] = useActionMutation<WorkOrder, StateTransitionArgs<WorkOrder>>({
    query,
    ...props,
  })

  const changeStatus = (targetState: WorkOrderStatusEnum, workOrderId: string, args?: DestructedTransitionArgs) => {
    const filter = { _id: workOrderId }
    return _changeStatus({ targetState, filter, args }, filter, workOrderId)
  }

  /**
   * Moves work order to In Progress status.
   */
  const moveToInProgress = (_id: string) => {
    return changeStatus(WorkOrderStatusEnum.InProgress, _id)
  }

  /**
   * Moves work order to Complete status.
   * This triggers inventory consumption and product build.
   */
  const moveToComplete = (_id: string) => {
    return changeStatus(WorkOrderStatusEnum.Complete, _id)
  }

  /**
   * Moves work order to Cancelled status.
   * If previous status is Complete, rollback is performed first.
   */
  const moveToCancel = (_id: string) => {
    return changeStatus(WorkOrderStatusEnum.Cancelled, _id)
  }

  /**
   * Moves work order back to Draft status.
   * If previous status is Complete, rollback is performed first.
   */
  const moveToDraft = (_id: string) => {
    return changeStatus(WorkOrderStatusEnum.Draft, _id)
  }

  /**
   * Moves work order to Destructed status.
   * Reverses the build, applying the given scrapped items.
   */
  const moveToDestructed = (_id: string, args: DestructedTransitionArgs) => {
    return changeStatus(WorkOrderStatusEnum.Destructed, _id, args)
  }

  return [
    {
      // Generic mutation function
      changeStatus,

      // Convenience wrapper functions
      moveToInProgress,
      moveToComplete,
      moveToCancel,
      moveToDraft,
      moveToDestructed,
    },
    result,
  ] as const
}
